import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import SafeIcon from '../../components/common/SafeIcon';
import { useAuth } from '../../context/AuthContext';
import {
  createReelDraft,
  createSessionDraft,
  fetchMyDraftReels,
  fetchMyDraftSessions,
  getVideoMetadata,
  uploadVideoToPublitio,
} from '../../services/training';

function FieldLabel({ children, required }) {
  return (
    <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">
      {children}
      {required && <span className="text-brand-cyan ml-1">*</span>}
    </label>
  );
}

export default function TrainingUploadPage() {
  const navigate = useNavigate();
  const { canManageTraining, loading: authLoading } = useAuth();

  const [mode, setMode] = useState('session');
  const [videoFile, setVideoFile] = useState(null);
  const [videoDuration, setVideoDuration] = useState(null);
  const [sourceAspectRatio, setSourceAspectRatio] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadPhase, setUploadPhase] = useState('');
  const [error, setError] = useState('');
  const [myDrafts, setMyDrafts] = useState({ sessions: [], reels: [] });

  useEffect(() => {
    if (!canManageTraining) return undefined;
    let active = true;
    Promise.all([fetchMyDraftSessions(), fetchMyDraftReels()])
      .then(([sessions, reels]) => {
        if (active) setMyDrafts({ sessions, reels });
      })
      .catch(console.error);
    return () => { active = false; };
  }, [canManageTraining]);

  useEffect(() => {
    if (!videoFile) {
      setVideoDuration(null);
      setSourceAspectRatio(null);
      setVideoPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(videoFile);
    setVideoPreviewUrl(url);
    getVideoMetadata(videoFile)
      .then(({ durationSeconds, aspectRatio }) => {
        setVideoDuration(durationSeconds);
        setSourceAspectRatio(aspectRatio);
      })
      .catch(() => {
        setVideoDuration(null);
        setSourceAspectRatio(null);
      });
    return () => URL.revokeObjectURL(url);
  }, [videoFile]);

  const handleUpload = async (e) => {
    e.preventDefault();
    setError('');
    if (!videoFile) {
      setError('Selecciona un archivo de video');
      return;
    }

    setSubmitting(true);
    setUploadPhase('Subiendo video a Publit.io…');

    try {
      const upload = await uploadVideoToPublitio(videoFile, {
        title: mode === 'session' ? 'Borrador de sesión' : 'Borrador de reel',
        tags: '',
        description: '',
      });

      if (!upload.previewUrl) {
        throw new Error('Publit.io no devolvió una URL de reproducción');
      }

      setUploadPhase('Creando borrador…');

      if (mode === 'standalone_reel') {
        const reelId = await createReelDraft({
          publitioVideoId: upload.publitioVideoId,
          videoUrl: upload.previewUrl,
          thumbnailUrl: upload.thumbnailUrl,
          sourceAspectRatio,
        });
        navigate(`/app/reels/${reelId}/borrador`, { replace: true });
        return;
      }

      const sessionId = await createSessionDraft({
        publitioVideoId: upload.publitioVideoId,
        videoUrl: upload.previewUrl,
        thumbnailUrl: upload.thumbnailUrl,
        durationSeconds: videoDuration,
      });
      navigate(`/app/entrenamientos/${sessionId}/borrador`, { replace: true });
    } catch (err) {
      setError(err.message || 'No se pudo subir el video');
    } finally {
      setSubmitting(false);
      setUploadPhase('');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-[var(--text-secondary)] text-sm font-bold uppercase tracking-widest">
        Cargando…
      </div>
    );
  }

  if (!canManageTraining) {
    return <Navigate to="/app/entrenamientos" replace />;
  }

  const draftCount = myDrafts.sessions.length + myDrafts.reels.length;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 pb-28">
      <div className="flex items-center gap-4 mb-8">
        <Link
          to="/app/entrenamientos"
          className="w-10 h-10 rounded-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] flex items-center justify-center text-[var(--text-main)] hover:bg-[var(--border-subtle)] transition-colors"
        >
          <SafeIcon name="ArrowLeft" className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-[var(--text-main)] uppercase tracking-tight italic">
            Subir video
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Paso 1 de 2 — sube el video. Luego completarás los detalles y podrás guardar como borrador o publicar.
          </p>
        </div>
      </div>

      {draftCount > 0 && (
        <section className="mb-8 p-5 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] space-y-3">
          <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)]">
            Tus borradores ({draftCount})
          </h2>
          <ul className="space-y-2">
            {myDrafts.sessions.map((s) => (
              <li key={s.id}>
                <Link
                  to={`/app/entrenamientos/${s.id}/borrador`}
                  className="flex items-center justify-between gap-3 text-sm text-[var(--text-main)] hover:text-brand-cyan"
                >
                  <span className="truncate font-medium">{s.title || 'Sesión sin título'}</span>
                  <span className="text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)] shrink-0">
                    Sesión
                  </span>
                </Link>
              </li>
            ))}
            {myDrafts.reels.map((r) => (
              <li key={r.id}>
                <Link
                  to={`/app/reels/${r.id}/borrador`}
                  className="flex items-center justify-between gap-3 text-sm text-[var(--text-main)] hover:text-brand-cyan"
                >
                  <span className="truncate font-medium">{r.caption || 'Reel sin caption'}</span>
                  <span className="text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)] shrink-0">
                    Reel
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex gap-2 mb-8 p-1 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-full w-fit">
        {[
          { id: 'session', label: 'Nueva sesión' },
          { id: 'standalone_reel', label: 'Reel independiente' },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            disabled={submitting}
            onClick={() => { setMode(tab.id); setError(''); }}
            className={`px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${
              mode === tab.id
                ? 'gfi-gradient-bg text-brand-navy shadow-md'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-main)]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-xl border border-red-400/40 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleUpload} className="space-y-8">
        <section className="p-6 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] space-y-4">
          <FieldLabel required>Video</FieldLabel>
          <input
            type="file"
            accept="video/*"
            disabled={submitting}
            onChange={(e) => setVideoFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-[var(--text-secondary)] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-[10px] file:font-black file:uppercase file:tracking-widest file:gfi-gradient-bg file:text-brand-navy"
          />
          {videoFile && (
            <p className="text-xs text-[var(--text-secondary)]">
              {videoFile.name}
              {videoDuration
                ? ` · ${Math.floor(videoDuration / 60)}:${String(Math.floor(videoDuration % 60)).padStart(2, '0')} min`
                : ''}
            </p>
          )}
          {videoPreviewUrl && (
            <video
              src={videoPreviewUrl}
              controls
              className="w-full rounded-xl border border-[var(--border-subtle)] max-h-64 bg-black"
            />
          )}
        </section>

        <button
          type="submit"
          disabled={submitting || !videoFile}
          className="w-full md:w-auto flex items-center justify-center gap-3 gfi-gradient-bg text-brand-navy px-10 py-4 rounded-full font-black text-xs uppercase tracking-widest transition-all hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 shadow-xl"
        >
          {submitting ? (
            <>
              <SafeIcon name="Loader" className="w-5 h-5 animate-spin" />
              {uploadPhase || 'Subiendo…'}
            </>
          ) : (
            <>
              <SafeIcon name="Upload" className="w-5 h-5" />
              Subir y continuar
            </>
          )}
        </button>
      </form>
    </div>
  );
}
